<template>
  <v-dialog :value="value" max-width="520" persistent @input="onDialogInput">
    <v-card class="run-group-dialog">
      <v-card-title class="px-6">
        <h5 class="primary-color mb-0">Re-run group</h5>
        <v-spacer />
        <v-btn icon aria-label="Close" :disabled="running" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="px-6 py-2">
        <p class="body-2 mb-1">
          Queues one run per video of <strong>{{ group ? group.name : 'the group' }}</strong> with
          the group's configuration and engines.
        </p>
        <p class="caption grey--text text--darken-1 mb-4">
          Group runs never promote and never change the video's active run or status. Videos with a
          run of this group already queued or running are skipped.
        </p>

        <v-alert v-if="group && !group.frozen_at" dense text type="info" class="mb-4">
          <v-icon small left>mdi-snowflake</v-icon>
          The group will be frozen on this first run (configuration and engines locked).
        </v-alert>

        <v-text-field
          v-model="batch"
          label="Batch"
          :placeholder="defaultBatch"
          persistent-placeholder
          hint="Leave empty to use '<group name>@<date time>'"
          persistent-hint
          dense
          outlined
          clearable
          class="mb-3 run-group-dialog__batch"
          :disabled="running"
        />
        <v-textarea
          v-model="note"
          label="Note"
          rows="2"
          auto-grow
          dense
          outlined
          counter="500"
          maxlength="500"
          hide-details="auto"
          :disabled="running"
        />

        <v-alert v-if="error" dense text type="error" class="mt-3 mb-0">{{ error }}</v-alert>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-6 py-3">
        <span class="caption grey--text text--darken-2">{{ countLabel }}</span>
        <v-spacer />
        <v-btn text class="cancel-modal" :disabled="running" @click="close">Cancel</v-btn>
        <v-btn
          class="ok-modal run-group-dialog__run"
          :loading="running"
          :disabled="running"
          @click="run"
        >
          <v-icon small left>mdi-play</v-icon>
          Launch
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import AnalysisGroupsService from '@/services/AnalysisGroupsService';
import { defaultGroupBatch } from '@/utils/groupResolution';

/**
 * Confirms and launches a group run (M4 contract 3, `POST /analysis-groups/:id/run`), optionally
 * restricted to `memberIds`. Emits `launched` with the 202 body `{ batch, accepted, skipped }`.
 */
export default {
  name: 'RunGroupDialog',

  props: {
    value: { type: Boolean, default: false },
    group: { type: Object, default: null },
    /** Restrict the run to these member ids (empty = every member). */
    memberIds: { type: Array, default: () => [] },
  },

  data() {
    return { batch: '', note: '', running: false, error: '' };
  },

  computed: {
    defaultBatch() {
      return this.group ? defaultGroupBatch(this.group.name) : '';
    },
    countLabel() {
      const n = this.memberIds.length || Number(this.group?.members ?? 0);
      if (!n) return '';
      return this.memberIds.length
        ? `${n} selected ${n === 1 ? 'video' : 'videos'}`
        : `${n} ${n === 1 ? 'video' : 'videos'} in the group`;
    },
  },

  watch: {
    value(open) {
      if (open) {
        this.batch = '';
        this.note = '';
        this.error = '';
        this.running = false;
      }
    },
  },

  methods: {
    onDialogInput(open) {
      if (!open) this.close();
    },
    close() {
      if (this.running) return;
      this.$emit('input', false);
    },
    async run() {
      if (!this.group || this.running) return;
      this.running = true;
      this.error = '';
      try {
        const response = await AnalysisGroupsService.run(this.group.id, {
          members: this.memberIds,
          batch: this.batch,
          note: this.note,
        });
        const body = response?.data?.data ?? response?.data ?? {};
        this.$emit('launched', {
          batch: body.batch || this.batch || this.defaultBatch,
          accepted: Array.isArray(body.accepted) ? body.accepted : [],
          skipped: Array.isArray(body.skipped) ? body.skipped : [],
        });
        this.$emit('input', false);
      } catch (error) {
        const data = error?.response?.data;
        this.error =
          data?.message || data?.error || error?.message || 'Could not launch the group run.';
      } finally {
        this.running = false;
      }
    },
  },
};
</script>
